import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, query, orderBy, limit, onSnapshot, where, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../services/firebase';
import { getSite } from '../config/sites';
import BookingForm from '../components/BookingForm';
import AppointmentCalendar from '../components/AppointmentCalendar';
import AdminDashboard from '../components/AdminDashboard';
import CustomerManager from '../components/CustomerManager';
import './HomePage.css';

// ── New-booking in-app notification helpers ──
const getServiceLabel = (apt) => apt?.serviceName || apt?.serviceId?.name || apt?.notes?.trim() || 'Appointment';

const formatShortDate = (key) => {
  if (!key) return '';
  const [y, m, d] = String(key).split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const playNotificationBeep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
  } catch {
    /* sound is optional */
  }
};

export default function HomePage() {
  const [currentView, setCurrentView] = useState('calendar');
  const [refreshKey, setRefreshKey] = useState(0);
  const seenIdsRef = useRef(new Set());
  const adminAlertInitRef = useRef(false);
  const { brand: brandName } = getSite();

  // Deep link from a push notification: /calendar?date=YYYY-MM-DD&appt=<appointmentId>
  const location = useLocation();
  const deepLinkParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const focusAppointmentId = deepLinkParams.get('appt');
  const focusDate = deepLinkParams.get('date');

  useEffect(() => {
    if (focusAppointmentId) setCurrentView('calendar');
  }, [focusAppointmentId]);

  const views = [
    { key: 'calendar', label: 'Calendar', shortcut: 'C' },
    { key: 'booking', label: 'New Appointment', shortcut: 'N' },
    { key: 'customers', label: 'Customers', shortcut: '⇧C' },
    { key: 'admin', label: 'Operations', shortcut: 'A' },
  ];

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    // Only trigger when no input/textarea/select is focused
    const tag = document.activeElement?.tagName || '';
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;

    const key = e.key.toLowerCase();
    if (key === 'c' && !e.shiftKey) setCurrentView('calendar');
    else if (key === 'n') setCurrentView('booking');
    else if (key === 'c' && e.shiftKey) setCurrentView('customers');
    else if (key === 'a') setCurrentView('admin');
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Real-time in-app notification when a new appointment is booked
  // (e.g., from the public website) — works on any view.
  useEffect(() => {
    const q = query(collection(db, 'appointments'), orderBy('createdAt', 'desc'), limit(20));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const currentIds = new Set();
        const fresh = [];
        snap.docs.forEach((docSnap) => {
          currentIds.add(docSnap.id);
          if (!seenIdsRef.current.has(docSnap.id)) fresh.push(docSnap);
        });
        const isFirst = seenIdsRef.current.size === 0;
        seenIdsRef.current = currentIds;
        if (isFirst) return;
        fresh.forEach((docSnap) => {
          const apt = docSnap.data();
          const client = apt?.clientName || 'A client';
          const service = getServiceLabel(apt);
          const date = formatShortDate(apt?.appointmentDateKey);
          const time = apt?.startTime || '';
          playNotificationBeep();
          toast(`📅 New booking: ${client} — ${service}${date ? ` on ${date}` : ''}${time ? ` at ${time}` : ''}`, {
            duration: 8000,
          });
          setRefreshKey((k) => k + 1);
        });
      },
      (error) => console.error('New booking notification listener failed:', error)
    );
    return unsubscribe;
  }, []);

  // Show admin alerts (e.g., possible booking spam) — new ones only.
  useEffect(() => {
    const q = query(collection(db, 'adminAlerts'), where('read', '==', false), limit(5));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        if (!adminAlertInitRef.current) {
          adminAlertInitRef.current = true;
          return;
        }
        snap.docChanges().forEach((change) => {
          if (change.type !== 'added') return;
          const data = change.doc.data();
          toast(`⚠️ ${data.message || 'Admin alert'}`, { duration: 10000 });
          updateDoc(change.doc.ref, { read: true }).catch(() => {});
        });
      },
      (error) => console.error('Admin alerts listener failed:', error)
    );
    return unsubscribe;
  }, []);

  return (
    <div className="home-page">
      <header className="navbar">
        <div className="navbar-content">
          <h1>{brandName}</h1>
          <nav>
            {views.map((view) => (
              <button
                key={view.key}
                className={currentView === view.key ? 'active' : ''}
                onClick={() => setCurrentView(view.key)}
                title={`${view.label} (${view.shortcut})`}
              >
                {view.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main>
        {currentView === 'booking' && <BookingForm />}
        {currentView === 'calendar' && (
          <AppointmentCalendar refreshKey={refreshKey} focusAppointmentId={focusAppointmentId} focusDate={focusDate} />
        )}
        {currentView === 'customers' && <CustomerManager />}
        {currentView === 'admin' && <AdminDashboard />}
      </main>

      <footer>
        <p>
          2026 {brandName}. All rights reserved. {' '}
          <a href="/privacy">Privacy Policy</a> | <a href="/ToS">Terms of Service</a>
        </p>
      </footer>
    </div>
  );
}
